import type { BoggleChallengeBoardDistributionDefinition } from '~/boggle/config/boardDistributions/boardDistributionDefinition'
import {
  BOARD_DISTRIBUTION_RESOLVERS,
  type BoggleBoardDistributionCompatibilityMode,
  type BoggleBoardDistributionResolverEntry,
} from '~/boggle/config/boardDistributions/boardDistributionResolvers'
import type { BoggleChallengeBoardDistributionFamilyId } from '~/boggle/config/boardDistributions/types'
import type {
  BoggleResolvedChallengeBoardDistribution,
  BoggleResolvedChallengeBonus,
  BoggleResolvedChallengeMod,
} from '~/boggle/config/challenges/modTypes'
import { BONUS_RESOLVERS, type BoggleBonusResolver } from '~/boggle/config/bonuses/bonusResolvers'
import type { BoggleChallengeBonusDefinition } from '~/boggle/config/bonuses/bonusDefinition'
import type { BoggleBonusValue, BoggleChallengeBonusId } from '~/boggle/config/bonuses/types'

// Pattern for new challenge mod modules: keep one family's authored resolve logic in its
// own file, export a focused resolver, and register it in boardDistributionResolvers /
// bonusResolvers before the main dispatcher.

export type BoggleChallengeBonusResolverContext = {
  selectedMods: readonly BoggleResolvedChallengeMod[]
}

export type BoggleResolvedModularBoardDistributionFamily = {
  compatibilityMode: BoggleBoardDistributionCompatibilityMode
  mod: BoggleResolvedChallengeBoardDistribution
  rngState: number
}

export function createSelectedModsBonusResolverContext(
  selectedMods: readonly BoggleResolvedChallengeMod[],
): BoggleChallengeBonusResolverContext {
  return {
    selectedMods,
  }
}

export function resolveModularBoggleChallengeBoardDistributionFamily(
  family: BoggleChallengeBoardDistributionDefinition,
  rngState: number,
): BoggleResolvedModularBoardDistributionFamily | null {
  const entry = BOARD_DISTRIBUTION_RESOLVERS[
    family.id as BoggleChallengeBoardDistributionFamilyId
  ] as BoggleBoardDistributionResolverEntry | undefined

  if (!entry) {
    return null
  }

  const resolved = entry.resolve(family, rngState)

  return {
    compatibilityMode: entry.compatibilityMode,
    ...resolved,
  }
}

export function resolveModularBoggleChallengeBonusFamily(
  family: BoggleChallengeBonusDefinition,
  rngState: number,
  context: BoggleChallengeBonusResolverContext,
  value: BoggleBonusValue,
) {
  const resolver = BONUS_RESOLVERS[family.id as BoggleChallengeBonusId] as
    | BoggleBonusResolver
    | undefined

  if (!resolver) {
    return null
  }

  return resolver(family, rngState, context, value) as {
    mod: BoggleResolvedChallengeBonus
    rngState: number
  } | null
}
